import React, { JSX, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import type { AppConfig } from '@/config/runtime-config';
import AppHeader from '@/components/common/AppHeader';
import MembersPage from '@/pages/bol/management/members/MembersPage';
import HostsPage from '@/pages/bol/management/hosts/HostsPage';
import SubscriptionsPage from '@/pages/bol-notifications/subscriptions/SubscriptionsPage';
import ConsumersPage from '@/pages/bol-notifications/consumer/ConsumersPage';
import ConsumerProofsOfDeliveryPage from '@/pages/bol-notifications/consumer/ConsumerProofsOfDeliveryPage';
import DashboardPage from '@/pages/dashboards/DashboardPage';
import QuotationRequestsPage from '@/pages/bol/logistics-objects/quotation-request/QuotationRequestsPage';
import QuotationsPage from '@/pages/bol/logistics-objects/quotations/QuotationsPage';
import ServicesPage from '@/pages/bol/logistics-objects/services/ServicesPage';
import ActivitiesPage from '@/pages/bol/logistics-objects/activities/ActivitiesPage';
import ActivityDetailPage from '@/pages/bol/logistics-objects/activities/ActivityDetailPage';
import ConsignmentsPage from '@/pages/bol/logistics-objects/consignments/ConsignmentsPage';
import ConsignmentDetailPage from '@/pages/bol/logistics-objects/consignments/ConsignmentDetailPage';
import CustomerOrdersPage from '@/pages/bol/logistics-objects/customer-orders/CustomerOrdersPage';
import CustomerOrderDetailPage from '@/pages/bol/logistics-objects/customer-orders/CustomerOrderDetailPage';
import HumanMeansPage from '@/pages/bol/logistics-objects/human-means/HumanMeansPage';
import HumanMeanDetailPage from '@/pages/bol/logistics-objects/human-means/HumanMeanDetailPage';
import InvoicingDocumentsPage from '@/pages/bol/logistics-objects/invoicing-documents/InvoicingDocumentsPage';
import InvoicingDocumentDetailPage from '@/pages/bol/logistics-objects/invoicing-documents/InvoicingDocumentDetailPage';
import ItemsPage from '@/pages/bol/logistics-objects/items/ItemsPage';
import ItemDetailPage from '@/pages/bol/logistics-objects/items/ItemDetailPage';
import MaterialMeansPage from '@/pages/bol/logistics-objects/material-means/MaterialMeansPage';
import MaterialMeanDetailPage from '@/pages/bol/logistics-objects/material-means/MaterialMeanDetailPage';
import PiecesPage from '@/pages/bol/logistics-objects/pieces/PiecesPage';
import PieceDetailPage from '@/pages/bol/logistics-objects/pieces/PieceDetailPage';
import ProductsPage from '@/pages/bol/logistics-objects/products/ProductsPage';
import ProductDetailPage from '@/pages/bol/logistics-objects/products/ProductDetailPage';
import ServiceRequestsPage from '@/pages/bol/logistics-objects/service-requests/ServiceRequestsPage';
import ServiceRequestDetailPage from '@/pages/bol/logistics-objects/service-requests/ServiceRequestDetailPage';
import ServiceDetailPage from '@/pages/bol/logistics-objects/services//ServiceDetailPage';
import QuotationDetailPage from '@/pages/bol/logistics-objects/quotations/QuotationDetailPage';
import QuotationRequestDetailPage from '@/pages/bol/logistics-objects/quotation-request/QuotationRequestDetailPage';
import TokenDebugger from '@/components/TokenDebugger';
import OidcCallbackPage from '@/pages/auth/OidcCallbackPage';
import OidcSilentCallbackPage from '@/pages/auth/OidcSilentCallbackPage';
import LoginPage from '@/pages/login/LoginPage';
import { useAuth, AuthProvider } from '@/contexts/AuthContext';
import { SnackbarProvider } from '@/contexts/SnackbarContext';
import { loadConfig } from '@/config/runtime-config';
import { createMsalConfig } from '@/config/msalConfig';

function RequireAuth({ children }: { readonly children: JSX.Element }) {
  const { isAuthenticated, isLoading, accessToken, error } = useAuth();
  const location = useLocation();
  
  // Show loading while MSAL is processing or acquiring token
  if (isLoading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>Loading...</Typography>
      </Box>
    );
  }
  
  // If token acquisition failed, force re-authentication
  if (isAuthenticated && !accessToken && error) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Wait for token if authenticated but no token yet
  if (isAuthenticated && !accessToken) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>Acquiring access token...</Typography>
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
}

function ConfigLoader({ children }: { readonly children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeMsal = async () => {
      try {
        const loadedConfig = await loadConfig();
        document.title = loadedConfig.appTitle || 'BOL Backoffice';
        setConfig(loadedConfig);

        if (loadedConfig.authProvider === 'azure') {
          const msalConfig = createMsalConfig(loadedConfig);
          const instance = new PublicClientApplication(msalConfig);

          await instance.initialize();
          await instance.handleRedirectPromise();

          setMsalInstance(instance);
        }
      } catch (err) {
        console.error('Failed to initialize:', err);
        setError(err instanceof Error ? err.message : 'Initialization failed');
      } finally {
        setIsLoading(false);
      }
    };

    void initializeMsal();
  }, []);

  if (isLoading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography>Loading configuration...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
        gap={2}
      >
        <Typography color="error" variant="h6">
          Configuration Error
        </Typography>
        <Typography color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="100vh"
      >
        <Typography color="error">
          Failed to initialize authentication
        </Typography>
      </Box>
    );
  }

  if (config.authProvider === 'azure') {
    if (!msalInstance) {
      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          minHeight="100vh"
        >
          <Typography color="error">
            Failed to initialize authentication
          </Typography>
        </Box>
      );
    }

    return (
      <MsalProvider instance={msalInstance}>
        {children}
      </MsalProvider>
    );
  }

  return <>{children}</>;
}

function App() {
  return (
    <ConfigLoader>
      <AuthProvider>
        <SnackbarProvider>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            height: '100vh',
            overflow: 'hidden'
          }}>
            {/* Fixed Header */}
            <Box sx={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              zIndex: 1200 
            }}>
              <AppHeader />
            </Box>
            
            {/* Main Content Area - offset by header height */}
            <Box 
              component="main" 
              sx={{ 
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }}
            >
              <Routes>
                <Route path="/auth/callback" element={<OidcCallbackPage />} />
                <Route path="/auth/silent-callback" element={<OidcSilentCallbackPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={
                  <RequireAuth>
                    <DashboardPage />
                  </RequireAuth>
                } />
                <Route path="/members" element={
                  <RequireAuth>
                    <MembersPage />
                  </RequireAuth>
                } />
                <Route path="/hosts" element={
                  <RequireAuth>
                    <HostsPage />
                  </RequireAuth>
                } />
                <Route path="/subscriptions" element={
                  <RequireAuth>
                    <SubscriptionsPage />
                  </RequireAuth>
                } />
                <Route path="/consumers" element={
                  <RequireAuth>
                    <ConsumersPage />
                  </RequireAuth>
                } />
                <Route path="/consumer-proofs-of-delivery" element={
                  <RequireAuth>
                    <ConsumerProofsOfDeliveryPage />
                  </RequireAuth>
                } />
                <Route path="/quotation-requests" element={
                  <RequireAuth>
                    <QuotationRequestsPage />
                  </RequireAuth>
                } />
                <Route path="/quotation-requests/:quotationRequestId" element={
                  <RequireAuth>
                    <QuotationRequestDetailPage />
                  </RequireAuth>
                } />
                <Route path="/quotations" element={
                  <RequireAuth>
                    <QuotationsPage />
                  </RequireAuth>
                } />
                <Route path="/quotations/:quotationId" element={
                  <RequireAuth>
                    <QuotationDetailPage />
                  </RequireAuth>
                } />
                <Route path="/services" element={
                  <RequireAuth>
                    <ServicesPage />
                  </RequireAuth>
                } />
                <Route path="/services/:serviceId" element={
                  <RequireAuth>
                    <ServiceDetailPage />
                  </RequireAuth>
                } />
                <Route path="/activities" element={
                  <RequireAuth>
                    <ActivitiesPage />
                  </RequireAuth>
                } />
                <Route path="/activities/:activityId" element={
                  <RequireAuth>
                    <ActivityDetailPage />
                  </RequireAuth>
                } />
                <Route path="/consignments" element={
                  <RequireAuth>
                    <ConsignmentsPage />
                  </RequireAuth>
                } />
                <Route path="/consignments/:consignmentId" element={
                  <RequireAuth>
                    <ConsignmentDetailPage />
                  </RequireAuth>
                } />
                <Route path="/customer-orders" element={
                  <RequireAuth>
                    <CustomerOrdersPage />
                  </RequireAuth>
                } />
                <Route path="/customer-orders/:customerOrderId" element={
                  <RequireAuth>
                    <CustomerOrderDetailPage />
                  </RequireAuth>
                } />
                <Route path="/human-means" element={
                  <RequireAuth>
                    <HumanMeansPage />
                  </RequireAuth>
                } />
                <Route path="/human-means/:humanMeanId" element={
                  <RequireAuth>
                    <HumanMeanDetailPage />
                  </RequireAuth>
                } />
                <Route path="/invoicing-documents" element={
                  <RequireAuth>
                    <InvoicingDocumentsPage />
                  </RequireAuth>
                } />
                <Route path="/invoicing-documents/:invoicingDocumentId" element={
                  <RequireAuth>
                    <InvoicingDocumentDetailPage />
                  </RequireAuth>
                } />
                <Route path="/items" element={
                  <RequireAuth>
                    <ItemsPage />
                  </RequireAuth>
                } />
                <Route path="/items/:itemId" element={
                  <RequireAuth>
                    <ItemDetailPage />
                  </RequireAuth>
                } />
                <Route path="/material-means" element={
                  <RequireAuth>
                    <MaterialMeansPage />
                  </RequireAuth>
                } />
                <Route path="/material-means/:materialMeanId" element={
                  <RequireAuth>
                    <MaterialMeanDetailPage />
                  </RequireAuth>
                } />
                <Route path="/pieces" element={
                  <RequireAuth>
                    <PiecesPage />
                  </RequireAuth>
                } />
                <Route path="/pieces/:pieceId" element={
                  <RequireAuth>
                    <PieceDetailPage />
                  </RequireAuth>
                } />
                <Route path="/products" element={
                  <RequireAuth>
                    <ProductsPage />
                  </RequireAuth>
                } />
                <Route path="/products/:productId" element={
                  <RequireAuth>
                    <ProductDetailPage />
                  </RequireAuth>
                } />
                <Route path="/service-requests" element={
                  <RequireAuth>
                    <ServiceRequestsPage />
                  </RequireAuth>
                } />
                <Route path="/service-requests/:serviceRequestId" element={
                  <RequireAuth>
                    <ServiceRequestDetailPage />
                  </RequireAuth>
                } />
                <Route path="/debug" element={<TokenDebugger />} />
              </Routes>
            </Box>
          </Box>
        </SnackbarProvider>
      </AuthProvider>
    </ConfigLoader>
  );
}

export default App;
